'use client'

import { TourProvider } from '@reactour/tour'
import { ArrowLeft, ArrowRight, X } from 'lucide-react'
import { ReactNode } from 'react'
import { Button } from './button'

function TourStepContent({
  children,
  title,
}: {
  children: ReactNode
  title: string
}) {
  return (
    <div className="pr-8 text-white">
      <span className="mb-4 block text-lg">{title}</span>
      {children}
    </div>
  )
}

export default function TourWrapper({ children }: { children: ReactNode }) {
  return (
    <TourProvider
      padding={{ popover: [16, 16] }}
      styles={{
        popover: (base) => ({
          ...base,
          borderRadius: 12,
          backgroundColor: `hsl(240, 3%, 20%)`,
        }),
        badge: (base) => ({
          ...base,
          backgroundColor: `hsl(238, 85%, 62%)`,
        }),
        dot: (base, { current }: any) => ({
          ...base,
          background: current ? 'hsl(238, 85%, 62%)' : '',
        }),
      }}
      components={{
        Close: (props) => {
          return (
            <Button
              {...props}
              variant={'ghost'}
              size={'icon'}
              style={{ position: 'absolute', right: 12, top: 12 }}
            >
              <X />
            </Button>
          )
        },
        Arrow: (props) => {
          if (props.inverted) {
            return (
              <ArrowRight
                className={`!size-4 ${props.disabled ? 'text-gray-500' : 'text-white'} `}
              />
            )
          } else {
            return (
              <ArrowLeft
                className={`!size-4 ${props.disabled ? 'text-gray-500' : 'text-white'} `}
              />
            )
          }
        },
      }}
      steps={[
        {
          selector: '[data-tour-id="/home"]',
          content: (
            <TourStepContent title="Home">
              Get a quick overview of your studio—see your daily schedule,
              finances, and recent activity at a glance.
            </TourStepContent>
          ),
          position: 'right',
        },
        {
          selector: '[data-tour-id="/calendar"]',
          content: (
            <TourStepContent title="Calendar">
              View and manage all your lessons and events in one unified
              calendar.
            </TourStepContent>
          ),
          position: 'right',
        },
        {
          selector: '[data-tour-id="/students"]',
          content: (
            <TourStepContent title="Students">
              Access your full list of students—track their progress, manage
              lessons, and view contact details.
            </TourStepContent>
          ),
          position: 'right',
        },
        {
          selector: '[data-tour-id="/notes"]',
          content: (
            <TourStepContent title="Notes">
              Keep detailed notes for each student, tied to specific lessons or
              general observations.
            </TourStepContent>
          ),
          position: 'right',
        },
        {
          selector: '[data-tour-id="/events"]',
          content: (
            <TourStepContent title="Events">
              Plan and organize studio-wide events like recitals, group classes,
              or workshops.
            </TourStepContent>
          ),
          position: 'right',
        },
        {
          selector: '[data-tour-id="/groups"]',
          content: (
            <TourStepContent title="Groups">
              Manage groups of students for ensemble lessons or special
              programs.
            </TourStepContent>
          ),
          position: 'right',
        },
        {
          selector: '[data-tour-id="/attendance"]',
          content: (
            <TourStepContent title="Attendance">
              Easily track attendance for each lesson or event and ensure
              accurate records.
            </TourStepContent>
          ),
          position: 'right',
        },
        {
          selector: '[data-tour-id="/messages"]',
          content: (
            <TourStepContent title="Messages">
              Communicate with students or parents directly through Cadence
              Calendar's messaging center.
            </TourStepContent>
          ),
          position: 'right',
        },
        {
          selector: '[data-tour-id="/settings"]',
          content: (
            <TourStepContent title="Settings">
              Customize your studio preferences—manage your profile,
              availability, and integrations.
            </TourStepContent>
          ),
          position: 'right',
        },
        {
          selector: '[data-tour-id="/accounts"]',
          content: (
            <TourStepContent title="Accounts">
              Keep track of your student accounts and their financial activity.
            </TourStepContent>
          ),
          position: 'right',
        },
        {
          selector: '[data-tour-id="/transactions"]',
          content: (
            <TourStepContent title="Transactions">
              View and manage all financial transactions, including payments and
              charges.
            </TourStepContent>
          ),
          position: 'right',
        },
        {
          selector: '[data-tour-id="/invoices"]',
          content: (
            <TourStepContent title="Invoices">
              Create, send, and manage invoices for lessons, events, and other
              services.
            </TourStepContent>
          ),
          position: 'right',
        },
      ]}
    >
      {children}
    </TourProvider>
  )
}
